import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  Chip,
  Popover,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import enLocale from 'date-fns/locale/en-US';
import { differenceInCalendarDays, format, isAfter, isBefore, isValid, startOfDay } from 'date-fns';
import { REPORTS_MAX_RANGE_DAYS, REPORTS_MIN_DATE } from './useReportFilters';
import { formatRange } from './CalendarHeatmap';
import ReportsRangeCalendar from './ReportsRangeCalendar';
import SegmentedDateField from './SegmentedDateField';
import useFirstFittingVariant from './useFirstFittingVariant';

// Draft dates can be null or an Invalid Date while a field is half-typed.
const isDateValue = (value) => value instanceof Date && isValid(value);

// Below this width the desktop row can't fit labelled actions, so tabs pass
// iconOnlyActions; shared so every Reports tab switches at the same width.
export const ICON_ONLY_ACTIONS_BELOW_PX = 1000;

// Short preset labels, for phones too narrow for the full ones (Custom keeps
// its label: there's no shorter word for it).
const PRESET_SHORT_LABELS = { week: '1W', '15days': '15D', month: '1M', '3months': '3M' };

// Preset row variants, roomiest first. Normally only the first is used; with
// `fitPresetsToRow` the bar shows the first one that fits beside the actions.
const PRESET_VARIANTS = [
  { short: false, px: { xs: 1, sm: 2 } },   // today's buttons
  { short: false, px: { xs: 0.5, sm: 1 } }, // same labels, tighter padding
  { short: true, px: { xs: 1, sm: 2 } },
  { short: true, px: { xs: 0.5, sm: 1 } },
];

// Holds the hidden preset rows useFirstFittingVariant measures. Zero-size and clipping,
// so their natural width is measurable without taking space or scrolling.
const presetGhostLayerSx = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: 0,
  height: 0,
  overflow: 'hidden',
  visibility: 'hidden',
  pointerEvents: 'none',
};

// Hidden period-chip copies for useFirstFittingVariant: absolutely positioned in
// the clipping slot, so they take no space and stay out of the tab order.
const ghostLayerSx = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: 'max-content',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  visibility: 'hidden',
  pointerEvents: 'none',
};

// Same look as the date-range chip in the PnL calendar header (CalendarHeatmap),
// so the period reads identically across the Reports tabs.
const periodChipSx = {
  height: 24,
  maxWidth: 'none',
  fontSize: '0.7rem',
  fontWeight: 500,
  color: 'text.secondary',
  borderColor: 'divider',
  '& .MuiChip-label': { px: 1.25 },
};

const actionButtonSx = {
  borderRadius: 2,
  textTransform: 'none',
  px: { xs: 1, sm: 2 },
  py: { xs: 0.25, sm: 0.5 },
  fontSize: { xs: '0.65rem', sm: '0.8125rem' }
};

// Icon-only action button; its label becomes the tooltip and the aria-label.
const IconActionButton = ({ label, icon, badgeContent = 0, sx = {}, ...buttonProps }) => (
  <Tooltip title={label}>
    {/* A disabled button gets no pointer events, so the tooltip anchors on this wrapper. */}
    <Box component="span" sx={{ display: 'inline-flex' }}>
      <Button
        aria-label={label}
        size="small"
        sx={{ ...actionButtonSx, minWidth: 0, px: 0.75, ...sx }}
        {...buttonProps}
      >
        <Badge
          badgeContent={badgeContent}
          color="primary"
          sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', height: 14, minWidth: 14, px: 0.5 } }}
        >
          {icon}
        </Badge>
        {/* Zero-width text keeps the button's line box, so it stays exactly as
            tall as the labelled buttons beside it under any theme font. */}
        {'\u200B'}
      </Button>
    </Box>
  </Tooltip>
);

// Exported so a tab can show the same Filters button outside the bar (e.g. in
// Trade History's exchange row).
export const FiltersButton = ({ count, onClick, iconOnly = false }) => (iconOnly ? (
  <IconActionButton
    label={count > 0 ? `Filters (${count})` : 'Filters'}
    icon={<FilterListIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
    badgeContent={count}
    variant="outlined"
    color="primary"
    onClick={onClick}
  />
) : (
  <Button
    variant="outlined"
    color="primary"
    startIcon={<FilterListIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
    onClick={onClick}
    size="small"
    sx={actionButtonSx}
  >
    Filters {count > 0 && '(' + count + ')'}
  </Button>
));

/**
 * @param {Object}    props.filterProps          - Return value of useReportFilters.
 * @param {Function}  [props.onExportClick]      - Shows the Export button when provided.
 * @param {boolean}   [props.showFilterDrawerButton=true] - Shows the Filters (drawer) button.
 * @param {Function}  [props.onReset]            - Shows a Reset button when provided.
 * @param {boolean}   [props.resetDisabled]
 * @param {Date}      [props.minDate]            - Earliest selectable custom start date.
 * @param {number}    [props.maxRangeDays]       - Longest custom range in days (both ends counted).
 * @param {boolean}   [props.showActivePills=true] - Show the active-filter chip row.
 * @param {boolean}   [props.iconOnlyActions=false] - Filters/Export/Reset as icon buttons.
 * @param {boolean}   [props.showPeriodChip=false] - Period chip after the presets, shortened to fit.
 * @param {boolean}   [props.fitPresetsToRow=false] - Presets shrink to share one row with the actions.
 */
const ReportsFilterBar = ({
  filterProps,
  onExportClick,
  showFilterDrawerButton = true,
  onReset,
  resetDisabled = false,
  minDate = REPORTS_MIN_DATE,
  maxRangeDays = REPORTS_MAX_RANGE_DAYS,
  showActivePills = true,
  iconOnlyActions = false,
  showPeriodChip = false,
  fitPresetsToRow = false,
}) => {
  const theme = useTheme();
  const {
    presets,
    selectedPreset,
    handlePresetClick,
    customPopoverAnchor,
    setCustomPopoverAnchor,
    handleCustomDateApply,
    setFilterDrawerOpen,
    activeFilterCount,
    activeFilterPills,
    startDate,
    endDate
  } = filterProps;

  const [tempStartDate, setTempStartDate] = useState(startDate || new Date());
  const [tempEndDate, setTempEndDate] = useState(endDate || new Date());
  // Which date box the calendar fills: Start first, then End.
  const [activeField, setActiveField] = useState('start');

  useEffect(() => {
    if (customPopoverAnchor) {
      setTempStartDate(startDate);
      setTempEndDate(endDate);
      setActiveField('start');
    }
  }, [customPopoverAnchor, startDate, endDate]);

  // `today` is fixed per opening so the max date stays stable while the popover is open.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const today = useMemo(() => new Date(), [customPopoverAnchor]);
  const hasDraftStart = isDateValue(tempStartDate);
  const hasDraftEnd = isDateValue(tempEndDate);
  const draftDays = hasDraftStart && hasDraftEnd
    ? differenceInCalendarDays(tempEndDate, tempStartDate) + 1
    : 0;
  // Typed dates can land outside what the calendar allows, so check them.
  const outOfBounds = (date) => isBefore(startOfDay(date), startOfDay(minDate))
    ? 'early'
    : isAfter(startOfDay(date), startOfDay(today)) ? 'late' : null;
  const startProblem = hasDraftStart ? outOfBounds(tempStartDate) : null;
  const endProblem = hasDraftEnd ? outOfBounds(tempEndDate) : null;
  const endBeforeStart = hasDraftStart && hasDraftEnd && draftDays <= 0;
  const rangeTooLong = Boolean(maxRangeDays) && draftDays > maxRangeDays;
  const rangeError = startProblem === 'early' || endProblem === 'early'
    ? `Dates can't be before ${format(minDate, 'dd/MM/yyyy')}`
    : startProblem === 'late' || endProblem === 'late'
      ? "Dates can't be after today"
      : endBeforeStart
        ? 'End date must be on or after the start date'
        : rangeTooLong ? `You can select up to ${maxRangeDays} days at a time. Pick a shorter range.` : null;
  const canApplyRange = hasDraftStart && hasDraftEnd && !rangeError;
  const rangeHint = !hasDraftStart
    ? 'Select a start date'
    : !hasDraftEnd || endBeforeStart
      ? 'Select an end date'
      : `${draftDays} ${draftDays === 1 ? 'day' : 'days'} selected`;

  // Period chip: the calendar header's range labels, full then short.
  const periodRange = showPeriodChip && isValid(startDate) && isValid(endDate)
    ? formatRange(startDate, endDate)
    : null;
  const periodLabels = periodRange ? [periodRange.full, periodRange.short] : null;
  const periodSlotRef = useRef(null);
  const periodGhostRefs = [useRef(null), useRef(null)];
  const periodLabelIndex = useFirstFittingVariant(periodSlotRef, periodGhostRefs, Boolean(periodLabels));

  // Presets that fit beside the actions (fitPresetsToRow). If even the
  // tightest variant doesn't fit, it's used anyway and the row scrolls.
  const presetSlotRef = useRef(null);
  const presetGhostRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];
  const presetFitIndex = useFirstFittingVariant(presetSlotRef, presetGhostRefs, fitPresetsToRow);
  const presetVariant = fitPresetsToRow
    ? PRESET_VARIANTS[Math.min(presetFitIndex, PRESET_VARIANTS.length - 1)]
    : PRESET_VARIANTS[0];

  const renderPresetButton = (key, config, variant, ghost = false) => {
    const isActive = selectedPreset === key;
    const shortLabel = variant.short ? PRESET_SHORT_LABELS[key] : undefined;
    return (
      <Button
        key={key}
        variant={isActive ? 'contained' : 'outlined'}
        onClick={ghost ? undefined : (e) => {
          if (key === 'custom') {
            setCustomPopoverAnchor(e.currentTarget);
          } else {
            handlePresetClick(key);
          }
        }}
        tabIndex={ghost ? -1 : undefined}
        // A short label ("1W") still announces the full name.
        aria-label={shortLabel ? config.label : undefined}
        size="small"
        disableElevation
        sx={{
          borderRadius: 2,
          textTransform: 'none',
          minWidth: 'max-content',
          px: variant.px,
          py: { xs: 0.25, sm: 0.5 },
          fontSize: { xs: '0.65rem', sm: '0.8125rem' },
          fontWeight: isActive ? 600 : 500,
          bgcolor: isActive ? theme.palette.primary.main : 'transparent',
          color: isActive ? theme.palette.primary.contrastText : theme.palette.text.primary,
          borderColor: isActive ? theme.palette.primary.main : theme.palette.divider,
          '&:hover': {
            bgcolor: isActive ? theme.palette.primary.dark : theme.palette.action.hover,
            borderColor: isActive ? theme.palette.primary.dark : theme.palette.divider,
          }
        }}
      >
        {shortLabel || config.label}
      </Button>
    );
  };

  const hasActions = showFilterDrawerButton || Boolean(onExportClick) || Boolean(onReset);

  return (
    <>
      {/* rowGap leaves room for an outlined field's floating label on wrapped rows */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        // With fitPresetsToRow the presets shrink instead, so nothing wraps.
        flexWrap: fitPresetsToRow ? 'nowrap' : 'wrap',
        position: fitPresetsToRow ? 'relative' : undefined,
        columnGap: { xs: 0.5, sm: 1 },
        rowGap: 1.25,
      }}>
        {fitPresetsToRow && (
          <Box aria-hidden sx={presetGhostLayerSx}>
            {PRESET_VARIANTS.map((variant, index) => (
              <Box
                key={index}
                ref={presetGhostRefs[index]}
                sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, width: 'max-content' }}
              >
                {Object.entries(presets).map(([key, config]) => renderPresetButton(key, config, variant, true))}
              </Box>
            ))}
          </Box>
        )}

        {/* Presets: full width on mobile, natural width from sm. With fitPresetsToRow
            it takes exactly the room the actions leave (flex-basis 0). */}
        <Box
          ref={presetSlotRef}
          sx={{
            display: 'flex',
            gap: { xs: 0.5, sm: 1 },
            ...(fitPresetsToRow
              ? { flex: '1 1 0', minWidth: 0, justifyContent: 'space-between' }
              : { width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'space-between', sm: 'flex-start' } }),
            overflowX: 'auto',
            '&::-webkit-scrollbar': { display: 'none' },
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
          }}
        >
          {Object.entries(presets).map(([key, config]) => renderPresetButton(key, config, presetVariant))}
        </Box>

        {/* flex-basis 0: the chip only uses leftover room, so it can't make the actions wrap. */}
        {periodLabels && (
          <Box
            ref={periodSlotRef}
            sx={{
              flex: '1 1 0',
              minWidth: 0,
              position: 'relative',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Box aria-hidden sx={ghostLayerSx}>
              {periodLabels.map((label, index) => (
                <Chip
                  key={index}
                  ref={periodGhostRefs[index]}
                  label={label}
                  size="small"
                  variant="outlined"
                  sx={periodChipSx}
                />
              ))}
            </Box>
            {periodLabelIndex < periodLabels.length && (
              <Tooltip title={periodLabelIndex > 0 ? periodLabels[0] : ''} disableInteractive>
                <Chip
                  label={periodLabels[periodLabelIndex]}
                  size="small"
                  variant="outlined"
                  sx={periodChipSx}
                />
              </Tooltip>
            )}
          </Box>
        )}

        {/* Action Buttons — pushed to the end of the row, sized to their content */}
        {hasActions && (
          <Box sx={{
            display: 'flex',
            gap: { xs: 0.5, sm: 1 },
            ml: 'auto',
            flexShrink: fitPresetsToRow ? 0 : undefined,
          }}>
            {showFilterDrawerButton && (
              <FiltersButton
                count={activeFilterCount}
                onClick={() => setFilterDrawerOpen(true)}
                iconOnly={iconOnlyActions}
              />
            )}
            {onExportClick && (iconOnlyActions ? (
              <IconActionButton
                label="Export"
                icon={<DownloadIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
                variant="outlined"
                color="secondary"
                onClick={onExportClick}
              />
            ) : (
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<DownloadIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
                onClick={onExportClick}
                size="small"
                sx={actionButtonSx}
              >
                Export
              </Button>
            ))}
            {onReset && iconOnlyActions && (
              <IconActionButton
                label="Reset"
                icon={<RefreshIcon sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }} />}
                variant="text"
                onClick={onReset}
                disabled={resetDisabled}
                sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
              />
            )}
            {onReset && !iconOnlyActions && (
              <Button
                variant="text"
                startIcon={<RefreshIcon sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }} />}
                onClick={onReset}
                disabled={resetDisabled}
                size="small"
                sx={{
                  ...actionButtonSx,
                  color: 'text.secondary',
                  '&:hover': { color: 'primary.main' }
                }}
              >
                Reset
              </Button>
            )}
          </Box>
        )}
      </Box>

      {/* Active Filters Strip */}
      {showActivePills && activeFilterPills.length > 0 && (
        <Box sx={{ display: { xs: 'none', sm: 'flex' }, flexWrap: 'wrap', gap: 1, mt: 1, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
          {activeFilterPills.map((pill) => (
            <Chip
              key={pill.key}
              label={pill.hideLabel ? pill.displayValue : `${pill.label}: ${pill.displayValue}`}
              onDelete={pill.onClear}
              size="small"
              variant="outlined"
              sx={{ borderRadius: 1 }}
            />
          ))}
        </Box>
      )}

      {/* Custom Date Popover */}
      <Popover
        open={Boolean(customPopoverAnchor)}
        anchorEl={customPopoverAnchor}
        onClose={() => setCustomPopoverAnchor(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          // 320px calendar + padding; on narrow phones it shrinks to the
          // screen and the calendar (280px of day grid) still fits at 360px.
          sx: { p: 2, mt: 1, width: 352, maxWidth: 'calc(100vw - 32px)', borderRadius: 2 }
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enLocale}>
          <Typography variant="subtitle2">Select Custom Range</Typography>
          {/* aria-live so screen readers hear the next step after each click */}
          <Typography
            variant="caption"
            color="text.secondary"
            aria-live="polite"
            sx={{ display: 'block', mb: 1.5 }}
          >
            {rangeHint}
          </Typography>

          {/* Typing and calendar clicks edit the same draft, so both always agree. */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <SegmentedDateField
              label="Start Date"
              value={tempStartDate}
              onChange={setTempStartDate}
              active={activeField === 'start'}
              onActivate={() => setActiveField('start')}
              error={Boolean(startProblem) || endBeforeStart || rangeTooLong}
            />
            <SegmentedDateField
              label="End Date"
              value={tempEndDate}
              onChange={setTempEndDate}
              active={activeField === 'end'}
              onActivate={() => setActiveField('end')}
              error={Boolean(endProblem) || endBeforeStart || rangeTooLong}
            />
          </Box>

          <ReportsRangeCalendar
            start={tempStartDate}
            end={tempEndDate}
            activeField={activeField}
            onChange={(newStart, newEnd, nextActiveField) => {
              setTempStartDate(newStart);
              setTempEndDate(newEnd);
              setActiveField(nextActiveField);
            }}
            minDate={minDate}
            maxDate={today}
          />

          {/* Right above the Apply it disables, so the reason is seen where the user looks. */}
          {rangeError && (
            <Alert
              severity="error"
              sx={{ mb: 1.5, py: 0, fontSize: '0.75rem', alignItems: 'center', '& .MuiAlert-icon': { fontSize: 18, mr: 1 } }}
            >
              {rangeError}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button size="small" onClick={() => setCustomPopoverAnchor(null)}>Cancel</Button>
            <Button
              variant="contained"
              size="small"
              disabled={!canApplyRange}
              onClick={() => handleCustomDateApply(tempStartDate, tempEndDate)}
            >
              Apply
            </Button>
          </Box>
        </LocalizationProvider>
      </Popover>
    </>
  );
};

export default ReportsFilterBar;
